<template>
  <section class="section" id="accesses-section">
    <div class="container">
      <b-loading
        :is-full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></b-loading>

      <accesses-title
        :accessesQty="accessesQty"
        :storeId="selectedStoreId"
        :storeName="selectedStoreName"
        @get-accesses="getAccesses"
        @update-dates="updateDates"
        @update-store="updateStore"
        @download-accesses-report="downloadAccessesReport"
        @update-accesses-filter="updateAccessesFilter"
      />

      <b-pagination
        v-show="accessesQty > 0"
        :current.sync="current"
        :total="accessesQty"
        :per-page="perPage"
        order="is-centered"
        aria-next-label="Próxima página"
        aria-previous-label="Página anterior"
        aria-page-label="Página"
        aria-current-label="Página atual"
        @change="changePage"
      ></b-pagination>

      <form
        v-show="accessesQty > 0"
        class="go-to-page"
        @submit.prevent="goToPage"
      >
        <span>Ir para página</span>
        <b-input
          v-model.number="pageInput"
          type="number"
          min="1"
          :max="totalPages"
        ></b-input>
        <span>de {{ totalPages }}</span>
        <button class="button" type="submit">Ir</button>
      </form>

      <h2
        v-if="accessesQty === 0"
        class="subtitle has-text-centered is-spaced"
        style="margin-top: 5rem;"
      >
        {{ $t("accesses.noAccesses") }}
      </h2>

      <div v-else>
        <h3
          v-if="accessesFilter === 'suspect-accesses'"
          class="has-text-centered"
        >
          {{ $t("accesses.suspectAccessesExplanation") }}
          <br /><br />
        </h3>

        <accesses-table :accesses="accesses" @loading="loading" />
      </div>

      <b-pagination
        v-show="accessesQty > 0"
        :current.sync="current"
        :total="accessesQty"
        :per-page="perPage"
        order="is-centered"
        aria-next-label="Próxima página"
        aria-previous-label="Página anterior"
        aria-page-label="Página"
        aria-current-label="Página atual"
        @change="changePage"
      ></b-pagination>
    </div>
  </section>
</template>

<script>
import { common } from "../../mixins/common";
import { mapGetters } from "vuex";
import accessesTitle from "./AccessesTitle";
import accessesTable from "./accesses-table/AccessesTable";

export default {
  mixins: [common],

  components: {
    accessesTitle,
    accessesTable
  },

  data() {
    return {
      isLoading: false,
      dates: [],
      store: undefined,
      storeName: null,
      accessesFilter: "",
      current: 1,
      pageInput: 1,
      perPage: 100,
      accessesQty: 0
    };
  },

  methods: {
    getAccesses(skip) {
      if (isNaN(skip)) {
        skip = (this.current - 1) * this.perPage;
      }
      this.isLoading = true;
      this.$store
        .dispatch("getAccesses", {
          storeId: this.store,
          dates: this.dates,
          skip,
          limit: this.perPage,
          filter: this.accessesFilter
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.accessesQty = res.data.payload.accessesQty;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateDates(dates) {
      this.dates[0] = dates
        ? this.moment(dates[0]).format("YYYY-MM-DD")
        : this.moment().format("YYYY-MM-DD");

      this.dates[1] = dates
        ? this.moment(dates[1]).format("YYYY-MM-DD")
        : this.moment().format("YYYY-MM-DD");
    },

    downloadAccessesReport() {
      this.isLoading = true;
      this.$store
        .dispatch("downloadAccessesReport", {
          storeName: this.storeName,
          storeId: this.store,
          dates: this.dates,
          filter: this.accessesFilter
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateStore(store) {
      this.store = store ? store.split("|")[0] : "";
      this.storeName = store ? store.split("|")[1] : "Todas as lojas";
    },

    updateAccessesFilter(filter) {
      this.accessesFilter = filter;
      this.current = 1;
      this.pageInput = 1;
      this.getAccesses(0);
    },

    loading(state) {
      this.isLoading = state;
    },

    changePage(p) {
      this.pageInput = p;
      const skip = (p - 1) * this.perPage;
      this.getAccesses(skip);
    },

    goToPage() {
      const page = Number(this.pageInput);
      const nextPage = isNaN(page)
        ? 1
        : Math.max(1, Math.min(page, this.totalPages));

      this.current = nextPage;
      this.pageInput = nextPage;
      this.changePage(nextPage);
    }
  },

  watch: {
    accesses() {
      if (!this.accesses.length) this.isLoading = false;
    }
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "accesses"]),

    isAllStoresMode() {
      return this.$route.query.allStores === "true";
    },

    selectedStoreId() {
      return this.isAllStoresMode ||
        !this.storeBeingManaged ||
        !this.storeBeingManaged._id
        ? null
        : this.storeBeingManaged._id;
    },

    selectedStoreName() {
      return this.selectedStoreId && this.storeBeingManaged.storeInfo
        ? this.storeBeingManaged.storeInfo.name
        : this.$t("accesses.allStores");
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.accessesQty / this.perPage));
    }
  },

  mounted() {
    this.updateDates();
    this.getAccesses();
  },

  created() {
    this.store = this.selectedStoreId || "";
    this.storeName = this.selectedStoreName;
  }
};
</script>

<style scoped>
#accesses-section {
  margin-top: 3rem;
  min-height: 94vh;
  padding: 2rem 1rem;
  background-color: hsl(0, 0%, 96%);
}

.go-to-page {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 12px 0 18px;
}

.go-to-page .control {
  width: 90px;
}
</style>
